// src/components/Common/OAuthCallback.tsx

import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginBox from './LoginBox';
import { useAuthenticateUser } from '../../hooks/useAuthenticateUser';
import { useDispatch } from 'react-redux';
import { resetMoodle } from '../../support/slices/moodleSlice';
import { hideReloginModal } from '../../support/slices/uiSlice';

const SALESFORCE_OAUTH_STATE_KEY = 'salesforce_oauth_state';

// Safely resolve the origin of a configured URL string.
const safeOrigin = (value?: string): string | null => {
  if (!value) return null;
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
};

const OAuthCallback = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleOAuthCallback } = useAuthenticateUser();

  // Guard so the effect body only runs a single time even under
  // React StrictMode double-invocation or re-renders.
  const handledRef = useRef(false);

  // When set on the OLD callback host, this forwards the raw Salesforce
  // authorization code (and state) to the NEW callback host, which then
  // performs the actual code exchange.
  //
  // Leave unset (or set to this app's own callback URL) on the NEW host.
  const forwardCallbackTo = process.env.REACT_APP_FORWARD_SALESFORCE_CALLBACK_TO;

  // The Salesforce-registered redirect URI. In local development this points
  // directly at this app (e.g. http://localhost:3001/ui/oauth/_callback), so
  // no bridge hop is needed and forwarding should be bypassed.
  const salesforceRedirectUri = process.env.REACT_APP_SALESFORCE_REDIRECT_URI;

  // Fix: Only dispatch on mount
  useEffect(() => {
    dispatch(resetMoodle());
    dispatch(hideReloginModal());
  }, [dispatch]);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const returnedState = urlParams.get('state');

    if (!code) {
      navigate('/', { replace: true });
      return;
    }

    // ---------------------------------------------------------------
    // Bridge step (runs on the OLD callback host only)
    //
    // Salesforce is configured to redirect only to the old callback URL.
    // The authorization code is short-lived and has NOT been exchanged yet,
    // so we can safely forward it to the new callback host, preserving the
    // full query string (code + state).
    //
    // Bypass conditions (no forwarding, exchange happens right here):
    //   1. REACT_APP_FORWARD_SALESFORCE_CALLBACK_TO is not set, OR
    //   2. The current origin already matches the registered Salesforce
    //      redirect URI origin. This covers local development where
    //      REACT_APP_SALESFORCE_REDIRECT_URI points directly at this app
    //      (e.g. http://localhost:3001/ui/oauth/_callback), OR
    //   3. The current origin already matches the forward target origin.
    // ---------------------------------------------------------------
    const currentOrigin = window.location.origin;
    const registeredRedirectOrigin = safeOrigin(salesforceRedirectUri);

    // If Salesforce is already redirecting straight to this app's origin,
    // there is no old->new hop to perform.
    const isRegisteredRedirectHost =
      registeredRedirectOrigin != null && currentOrigin === registeredRedirectOrigin;

    if (forwardCallbackTo && !isRegisteredRedirectHost) {
      try {
        const forwardUrl = new URL(forwardCallbackTo);

        const isAlreadyOnForwardHost =
          currentOrigin === forwardUrl.origin &&
          window.location.pathname.replace(/\/$/, '') ===
          forwardUrl.pathname.replace(/\/$/, '');

        if (!isAlreadyOnForwardHost) {
          // Preserve code + state exactly as Salesforce provided them.
          forwardUrl.search = location.search;
          window.location.replace(forwardUrl.toString());
          return;
        }
      } catch (e) {
        console.error(
          'Invalid REACT_APP_FORWARD_SALESFORCE_CALLBACK_TO:',
          forwardCallbackTo,
          e,
        );
        navigate('/', { replace: true });
        return;
      }
    }

    // ---------------------------------------------------------------
    // Exchange step (runs on the NEW callback host, or locally when the
    // bridge is bypassed)
    //
    // Validate state (if present) before exchanging the code. The backend
    // still uses its configured SALESFORCE_REDIRECT_URI for the token
    // exchange, so no backend changes are required.
    // ---------------------------------------------------------------
    const expectedState = localStorage.getItem(SALESFORCE_OAUTH_STATE_KEY);

    if (expectedState && returnedState && returnedState !== expectedState) {
      console.error(
        '[AUTH] Salesforce OAuth state mismatch:',
        `received=${returnedState} expected=${expectedState}`,
      );
      localStorage.removeItem(SALESFORCE_OAUTH_STATE_KEY);
      navigate('/', { replace: true });
      return;
    }

    // State validated (or not in use). Clear it and proceed.
    localStorage.removeItem(SALESFORCE_OAUTH_STATE_KEY);

    handleOAuthCallback(code)
      .then(() => {
        const path = localStorage.getItem('postLoginPath');
        if (path) {
          localStorage.removeItem('postLoginPath');
          navigate(path, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      })
      .catch((e) => {
        console.error('Authentication callback failed:', e);
        navigate('/', { replace: true });
      });
  }, [
    location.search,
    handleOAuthCallback,
    navigate,
    forwardCallbackTo,
    salesforceRedirectUri,
  ]);

  return (
    <LoginBox
      loading={true}
      buttonText="Toolbox Login"
      loadingText="Logging in..."
      showButton={false} // Hide button during OAuth callback
    />
  );
};

export default OAuthCallback;